import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useCreateSite } from '../hooks/useSitesRegistry'
import { toast } from 'sonner'
import { Plus, Loader2 } from 'lucide-react'

const addSiteSchema = z.object({
  domain: z
    .string()
    .min(1, 'Domain is required')
    .regex(/^[a-z0-9.-]+\.[a-z]{2,}$/i, 'Enter a valid domain (e.g., example.com)'),
  name: z.string().optional(),
  source: z.enum(['external', 'manual']).default('external'),
  notes: z.string().optional(),
})

type AddSiteFormData = z.infer<typeof addSiteSchema>

interface AddSiteDialogProps {
  trigger?: React.ReactNode
}

export function AddSiteDialog({ trigger }: AddSiteDialogProps) {
  const [open, setOpen] = useState(false)
  const createSite = useCreateSite()

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<AddSiteFormData>({
    resolver: zodResolver(addSiteSchema),
    defaultValues: {
      source: 'external',
    },
  })

  const sourceValue = watch('source')

  const onSubmit = async (data: AddSiteFormData) => {
    try {
      await createSite.mutateAsync({
        domain: data.domain.toLowerCase().trim(),
        name: data.name || undefined,
        source: data.source,
        notes: data.notes || undefined,
      })
      toast.success(`Site ${data.domain} added successfully`)
      setOpen(false)
      reset()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to add site')
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Add Site
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>Add External Site</DialogTitle>
            <DialogDescription>
              Register a WordPress site that isn't hosted on Rocket.net or managed via JezPress.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="domain">Domain</Label>
              <Input
                id="domain"
                placeholder="example.com"
                {...register('domain')}
              />
              {errors.domain && (
                <p className="text-sm text-destructive">{errors.domain.message}</p>
              )}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="name">Site Name (optional)</Label>
              <Input
                id="name"
                placeholder="My WordPress Site"
                {...register('name')}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="source">Source</Label>
              <Select
                value={sourceValue}
                onValueChange={(value) => setValue('source', value as 'external' | 'manual')}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="external">External</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                External = not managed by Jezweb, Manual = manually added
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                placeholder="Any relevant notes about this site..."
                {...register('notes')}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setOpen(false)
                reset()
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createSite.isPending}>
              {createSite.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                'Add Site'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
